import { TutorialDefinition, TutorialScene } from '../TutorialEngine.js';
import { COLORS } from '../../rendering/Renderer.js';
import { MirrorsVortexTutorialState } from './MirrorsTutorialAdapter.js';

type VState = MirrorsVortexTutorialState;

const PARTIAL = 'B0b,B4b,F4f,F1b';
const PARTIAL_GATED = `${PARTIAL}|E1=C2`;
const SOLVED = 'B0b,B4b,F4f,F1b,A2f,A5b,C0f,D0b,D5f,E5b|E1=C2';

// Activation scene: the main pair E1↔C2 is on empty cells (activatable), while a
// second pair B5↔D5 is blocked by a mirror on B5 (rendered amber, can't activate).
const ACTIVATE_BLOCKER = 'B5b';
const ACTIVATE_GATES: Array<[string, string]> = [['B5', 'D5']];

const scenes: TutorialScene[] = [
    // Scene 1: Toggle Gates — animate the gate placement flow
    {
        gameState: { mirrors: PARTIAL } as VState,
        renderOptions: { showLaser: true, showReverseLaser: false },
        annotations: [
            { type: 'text-label', text: 'Click Activate Vortex\nto place a gate pair', cell: 'D3', anchor: 'center' },
            { type: 'text-label', text: 'Pair must be linked\nin the Vortex Hub', cell: 'C5', anchor: 'center' },
        ],
        animate: {
            interval: 1500,
            loop: true,
            frames: [
                { mirrors: PARTIAL },
                { mirrors: PARTIAL, gateMode: true },
                { mirrors: PARTIAL_GATED, gateMode: true },
                { mirrors: PARTIAL_GATED },
            ] as VState[],
        },
    },

    // Scene 2: Activation — toggle an unblocked pair active; a blocked pair stays amber
    {
        gameState: { mirrors: ACTIVATE_BLOCKER, gateMode: true, extraGates: ACTIVATE_GATES } as VState,
        renderOptions: { showLaser: false, showReverseLaser: false },
        annotations: [
            { type: 'text-label', text: 'Click a pair\nto activate it', cell: 'E3', anchor: 'center', color: COLORS.teal },
            { type: 'text-label', text: 'Mirrors block\ngate activation', cell: 'B5', anchor: 'far-above', color: COLORS.amber },
        ],
        animate: {
            interval: 1500,
            loop: true,
            frames: [
                { mirrors: ACTIVATE_BLOCKER, gateMode: true, extraGates: ACTIVATE_GATES },
                { mirrors: `${ACTIVATE_BLOCKER}|E1=C2`, gateMode: true, extraGates: ACTIVATE_GATES },
            ] as VState[],
        },
    },

    // Scene 3: Beam teleportation
    {
        gameState: { mirrors: PARTIAL_GATED } as VState,
        renderOptions: { showLaser: true, showReverseLaser: false },
        annotations: [
            { type: 'text-label', text: 'Beam enters\na gate...', cell: 'E1', anchor: 'far-left' },
            { type: 'text-label', text: '...exits from\nthe other side', cell: 'C2', anchor: 'far-right' },
            { type: 'pulse', cell: 'E1', color: COLORS.purple },
            { type: 'pulse', cell: 'C2', color: COLORS.purple },
        ],
    },

    // Scene 4: Gates can be reused — full solved state
    {
        gameState: { mirrors: SOLVED } as VState,
        renderOptions: { showLaser: true, showReverseLaser: false },
        annotations: [
            { type: 'text-label', text: 'Gates can be used\nmultiple times', cell: 'D3', anchor: 'center', color: COLORS.teal },
            { type: 'pulse', cell: 'E1', color: COLORS.purple },
            { type: 'pulse', cell: 'C2', color: COLORS.purple },
        ],
    },
];

/**
 * Vortex Mirrors tutorial: 4 scenes on a 6×6 vortex grid with digits 0-3.
 * Teaches gate creation, activation, and beam teleportation through Vortex gates.
 */
export const MIRRORS_VORTEX_TUTORIAL: TutorialDefinition = {
    gameType: 'mirrors-vortex',
    gridCode: '6/0-3:0C4,1F2,2A3,3D1',
    scenes,
    previewScene: 3,
};
